import { useMemo, useState } from 'react'
import { MessageSquarePlus } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FeedbackAdminApi } from '../adminApi.js'
import type { AdminLoginResult } from '../../accounts/index.js'
import type { FeedbackCategory, FeedbackItem, ResolveFeedbackInput } from '../index.js'
import { FeedbackDrawer } from '../components/FeedbackDrawer.js'
import { FEEDBACK_CATEGORY_OPTIONS } from '../components/feedbackCategories.js'
import { adminFeedbackApi } from './adminFeedbackApi.js'
import { OpenFeedbackList } from './list/OpenFeedbackList.js'
import { FeedbackRow } from './list/FeedbackRow.js'
import { Button, navigate, EndpointHint, Skeleton } from '../../core/index.js'
import { changeHref } from './changes/changeText.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = FeedbackAdminApi

const QUERY_KEY = ['admin', 'feedback'] as const

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

/**
 * Everything people have asked for, and what became of it. Open items are
 * a priority list the admin drags into order; each can be completed or
 * discarded with a reason the person who asked will see. Resolved items
 * stay below, newest first, and can be reopened.
 */
interface FeedbackPageProps {
  api: AdminApi
  /** Who is signed in, for the screen context a new item carries. */
  session: AdminLoginResult
  /** Items submitted after this moment are tagged as new - the admin's previous sign-in; null tags everything, as on a first sign-in. */
  newSince?: string | null
}

function NewFeedbackButton({ onClick, prominent = false }: { onClick: () => void; prominent?: boolean }) {
  return (
    <Button icon={MessageSquarePlus} variant={prominent ? 'primary' : 'secondary'} size={prominent ? 'md' : 'sm'} onClick={onClick}>
      New feedback
    </Button>
  )
}

export function FeedbackPage({ api, session, newSince }: FeedbackPageProps) {
  const queryClient = useQueryClient()
  const feedbackQuery = useQuery({ queryKey: QUERY_KEY, queryFn: api.listFeedback })
  // The same form the floating icon opens; sending through it refreshes this list, since both read the same query.
  const [isComposing, setIsComposing] = useState(false)
  const feedbackApi = useMemo(() => adminFeedbackApi(api), [api])

  const resolve = useMutation({
    mutationFn: ({ feedbackId, decision }: { feedbackId: string; decision: ResolveFeedbackInput }) => api.resolveFeedback(feedbackId, decision),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  const reorder = useMutation({
    mutationFn: (feedbackIds: string[]) => api.reorderFeedback(feedbackIds),
    // The answer is the open items in their new order; the resolved ones are unchanged.
    onSuccess: (open) => {
      queryClient.setQueryData<FeedbackItem[]>(QUERY_KEY, (current) => [...open, ...(current ?? []).filter((item) => item.status !== 'open')])
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })

  // Gate 1: the new draft opens at once, with a way back here.
  const develop = useMutation({
    mutationFn: (feedbackId: string) => api.developFeedback(feedbackId),
    onSuccess: (change) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'changes'] })
      navigate(changeHref(change.changeRequestId), { rememberOrigin: true })
    },
  })

  // 'all', or one category: narrows both lists. Counts in the chooser are of everything, so the filter never hides its own options.
  const [categoryFilter, setCategoryFilter] = useState<FeedbackCategory | 'all'>('all')
  const items = feedbackQuery.data
  const matches = (item: FeedbackItem): boolean => categoryFilter === 'all' || item.category === categoryFilter
  const open = useMemo(() => (items ?? []).filter((item) => item.status === 'open' && matches(item)), [items, categoryFilter])
  const resolved = useMemo(() => (items ?? []).filter((item) => item.status !== 'open' && matches(item)), [items, categoryFilter])
  const countByCategory = useMemo(() => {
    const counts = new Map<FeedbackCategory, number>()
    for (const item of items ?? []) {
      counts.set(item.category, (counts.get(item.category) ?? 0) + 1)
    }
    return counts
  }, [items])
  const resolvingId = resolve.isPending ? resolve.variables.feedbackId : null
  const isNew = (item: FeedbackItem): boolean => newSince !== undefined && (newSince === null || item.submittedAt > newSince)

  return (
    <div className="px-6 py-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <p className="max-w-2xl text-xs leading-relaxed text-ink-mid">
            Sent from the feedback icon in the operator app and this admin area. Each item names who asked, as worked out from the credential they
            signed in with, and the screen they were on if they chose to include it. Drag open items into priority order; the order is saved as soon
            as you drop. A discarded item needs a reason, which the person who asked can see.
          </p>
          <NewFeedbackButton prominent onClick={() => setIsComposing(true)} />
        </div>

        {feedbackQuery.isSuccess && (items?.length ?? 0) > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by category">
            {[{ category: 'all' as const, label: 'All', count: items?.length ?? 0 }, ...FEEDBACK_CATEGORY_OPTIONS.map((option) => ({ category: option.category, label: option.label, count: countByCategory.get(option.category) ?? 0 }))]
              .filter((chip) => chip.category === 'all' || chip.count > 0)
              .map((chip) => (
                <button
                  key={chip.category}
                  type="button"
                  onClick={() => setCategoryFilter(chip.category)}
                  aria-pressed={categoryFilter === chip.category}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-coral ${
                    categoryFilter === chip.category ? 'border-marine-dk bg-marine-dk text-white' : 'border-marine/30 bg-white text-marine hover:bg-marine-lt/40'
                  }`}
                >
                  {chip.label} <span className={categoryFilter === chip.category ? 'text-white/70' : 'text-ink-mid'}>{chip.count}</span>
                </button>
              ))}
          </div>
        )}

        {resolve.isError && <p className="text-xs font-bold text-coral">{errorMessage(resolve.error, 'Could not update the item.')}</p>}
        {develop.isError && <p className="text-xs font-bold text-coral">{errorMessage(develop.error, 'Could not start a change for the item.')}</p>}
        {reorder.isError && <p className="text-xs font-bold text-coral">{errorMessage(reorder.error, 'Could not save the new order.')}</p>}

        <section className="bg-white border border-marine/15 rounded p-4">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <div className="text-xs font-extrabold text-ink">
              Open · {open.length} <EndpointHint>GET /admin/feedback · PUT /admin/feedback/order</EndpointHint>
            </div>
            {reorder.isPending && <span className="text-[11px] text-ink-mid">Saving order…</span>}
          </div>
          {feedbackQuery.isPending ? (
            <Skeleton label="Loading feedback" />
          ) : feedbackQuery.isError ? (
            <LoadFailure size="xs" message="Could not load the feedback." onRetry={() => void feedbackQuery.refetch()} retrying={feedbackQuery.isFetching} />
          ) : (
            <OpenFeedbackList
              items={open}
              isNew={isNew}
              emptyAction={<NewFeedbackButton onClick={() => setIsComposing(true)} />}
              resolvingId={resolvingId}
              onReorder={(feedbackIds) => reorder.mutate(feedbackIds)}
              onResolve={(feedbackId, decision) => resolve.mutate({ feedbackId, decision })}
              onDevelop={(feedbackId) => develop.mutate(feedbackId)}
              developingId={develop.isPending ? develop.variables : null}
            />
          )}
        </section>

        <section className="bg-white border border-marine/15 rounded p-4">
          <div className="mb-3 text-xs font-extrabold text-ink">
            Resolved · {resolved.length} <EndpointHint>PATCH /admin/feedback/{'{feedbackId}'}</EndpointHint>
          </div>
          {feedbackQuery.isSuccess && resolved.length === 0 ? (
            <p className="text-[11px] text-ink-mid">Nothing resolved yet.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {resolved.map((item) => (
                <li key={item.feedbackId} className="flex gap-2 rounded border border-marine/10 bg-canvas p-3">
                  <FeedbackRow item={item} isNew={isNew(item)} isResolving={resolvingId === item.feedbackId} onResolve={(decision) => resolve.mutate({ feedbackId: item.feedbackId, decision })} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {isComposing && (
        <FeedbackDrawer
          api={feedbackApi}
          aboveInspector
          details={{ role: session.role === 'root' ? 'SeeWhoCares root admin' : 'SeeWhoCares admin', admin: session.username, view: 'Product Feedback' }}
          onClose={() => setIsComposing(false)}
        />
      )}
    </div>
  )
}
